// Eskom PARCC — the standardised filter bar.
//
// Renders the nine controlled-vocabulary facets from feedback/Filters.xlsx
// (Dataset, Period, Reviewed, On Dashboard, Panel, Gaps Status, Compliance,
// Mechanism, Division) plus an optional free-text search and a Reset button,
// driven entirely by the shared filter model (src/domain/filters.ts). Reused by
// Dashboard, Transactions and Allocations so the three never drift.
//
// Purely controlled — the parent owns the FacetState and search string; this
// component only derives the (hybrid) option lists from the rows in view.
import { useMemo, type ReactNode } from 'react';
import { FilterBar, SelectFilter, SearchInput, Button } from './index';
import {
  FACET_DEFS,
  buildFacetOptions,
  type FacetState,
  type FacetRow,
  type FacetKey,
} from '../../domain/filters';

export interface FacetFilterBarProps {
  /** Rows in scope — used to build the hybrid (vocab + present outliers) option lists. */
  rows: readonly FacetRow[];
  state: FacetState;
  onChange: (next: FacetState) => void;
  onReset: () => void;
  /** Optional free-text search; the field only renders when `onSearch` is supplied. */
  search?: string;
  onSearch?: (q: string) => void;
  searchPlaceholder?: string;
  /** Restrict to a subset of facets (defaults to all nine, in spec order). */
  keys?: readonly FacetKey[];
  /** Extra controls rendered inside the bar (e.g. Allocations' reviewer/status selects). */
  children?: ReactNode;
}

export function FacetFilterBar({
  rows,
  state,
  onChange,
  onReset,
  search,
  onSearch,
  searchPlaceholder,
  keys,
  children,
}: FacetFilterBarProps): React.JSX.Element {
  const defs = useMemo(
    () => (keys ? FACET_DEFS.filter((d) => keys.includes(d.key)) : FACET_DEFS),
    [keys],
  );

  // Option lists recompute only when the rows in scope change — never per keystroke.
  const options = useMemo(() => {
    const map = {} as Record<FacetKey, { value: string; label: string }[]>;
    for (const def of defs) {
      map[def.key] = [
        { value: '', label: 'All' },
        ...buildFacetOptions(rows, def.key).map((v) => ({ value: v, label: v })),
      ];
    }
    return map;
  }, [rows, defs]);

  // ONE filter card. Previously this wrapped <FilterBar> (already a `.filters`
  // card in Lumen) in a SECOND `.filters` div, giving a card-inside-a-card with
  // Reset stranded in the gap between them. Reset now lives inside the single
  // card as the last item, pushed right (`.filter-reset` → margin-left:auto) and
  // bottom-aligned with the controls, so the bar is one full-width row that
  // matches the cards below it.
  return (
    <FilterBar>
      {defs.map((def) => (
        <SelectFilter
          key={def.key}
          label={def.label}
          value={state[def.key]}
          options={options[def.key]}
          onChange={(v) => onChange({ ...state, [def.key]: v })}
        />
      ))}
      {onSearch !== undefined && (
        <SearchInput
          label="Search"
          value={search ?? ''}
          placeholder={searchPlaceholder ?? 'Ref, supplier, division, mechanism…'}
          onChange={onSearch}
        />
      )}
      {children}
      <Button variant="ghost" onClick={onReset} className="filter-reset">
        Reset filters
      </Button>
    </FilterBar>
  );
}
